// pages/master-data/driver/MasterDriverAddEdit.jsx
'use client';
import { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import StorageIcon from '@mui/icons-material/Storage';

import { getDriverById, createDriver, updateDriver } from '../../../stores/driverStore';

import DriverForm from './form/DriverForm';
import DriverLicenseForm from './form/DriverLicenseForm';

import FormDialog from '../../../components/FormDialog';

import dayjs from 'dayjs';
import { useLanguage } from '../../../utils/i18n';
import { fleetApiGo, fleetApi } from '../../../api/axiosClient';
import { uploadDriverLicenseDetailFile, deleteDriverLicenseDetailImage } from '../../../stores/driverLicenseStore';
import { useNotifier } from '../../../components/Notifier';
import { formatUpperCase } from '../../../utils/formatTitleCase';

const getBaseInitial = () => {
  const today = dayjs().format('YYYY-MM-DD');
  return {
    company: null,
    name: '',
    driver_code: '',
    nik: '',
    rfid: '',
    mobile_no: '',
    phone_no: '',
    email: '',
    birth_place: '',
    birth_date: today,
    hired_date: today,
    is_pod_active: false,
    status: true,
    image: '',
    ktp_no: '',
    ktp_expdate: '',
    license_no: '',
    license_type: '',
    license_exp_date: today,
    latest_education: '',
    driver_type: '',
    vehicle: null,
    address: '',
    driver_type_id: null,
    driver_attachments: {
      bpjs_kesehatan: '',
      bpjs_ketenagakerjaan: '',
      kk: '',
      ktp: '',
      npwp: ''
    },
    driver_license: [],
    driver_license_removed: [],
    _images_to_delete: [],
    _image_file: null,
    _image_base64: '',
    id: null
  };
};

const toDateInput = (val) => {
  if (val == null || val === '') return '';
  const s = String(val).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const parsed = dayjs(s);
  if (parsed.isValid()) return parsed.format('YYYY-MM-DD');
  return '';
};

const normalizeDriverPayload = (values = {}, { forUpdate = false } = {}) => {
  const payload = {
    absent: !!values.absent,
    active: values.active === undefined ? true : !!values.active,
    address: values.address ?? values.address1 ?? '',
    birth_date: values.birth_date ?? values.birthdate ?? '',
    birth_place: values.birth_place ?? '',
    company: values.company != null ? Number(values.company) : null,
    driver_code: values.driver_code ?? values.code ?? '',
    driver_type: values.driver_type ?? '',
    driver_type_id: values.driver_type_id != null ? Number(values.driver_type_id) : 0,
    email: values.email ?? '',
    hired_date: values.hired_date ?? values.hireddate ?? '',
    id: values.id ?? undefined,
    image: '',
    is_pod_active: !!values.is_pod_active,
    ktp_no: values.ktp_no ?? values.nik ?? values.identity_number ?? '',
    ktp_expdate: values.ktp_expdate ?? '',
    license_no: values.license_no ?? '',
    license_type: values.license_type ?? '',
    license_exp_date: values.license_exp_date ?? values.license_expired ?? '',
    latest_education: values.latest_education ?? '',
    mobile_no: values.mobile_no ?? values.phone_no ?? values.phone ?? '',
    name: values.name ?? '',
    rfid: values.rfid ?? '',
    phone_no: values.phone_no ?? '',
    resign_date: values.resign_date ?? '',
    status: values.status === undefined ? true : !!values.status,
    username: values.username ?? ''
  };

  const dlArray = (Array.isArray(values.driver_license) ? values.driver_license : []).map((dl) => {
    return {
      id: dl.id ?? undefined,
      data_back_photo: dl.data_back_photo,
      data_front_photo: dl.data_front_photo,
      driver_license: dl.driver_license ?? dl.license_type ?? dl.license_id ?? undefined,
      file_license: dl.file_license,
      front_photo: '',
      back_photo: '',
      license_exp_date: dl.is_lifetime ? '' : dl.license_exp_date ? toDateInput(dl.license_exp_date) : '',
      license_name: dl.license_name,
      license_no: dl.license_no ?? '',
      url_license: dl.url_license,
      is_lifetime: Boolean(dl.is_lifetime)
    };
  });

  if (dlArray.length) payload.driver_license = dlArray;
  if (Array.isArray(values.driver_license_removed) && values.driver_license_removed.length) {
    payload.driver_license_removed = values.driver_license_removed;
  }

  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined) delete payload[k];
  });

  if (!forUpdate) {
    delete payload.id;
  }

  return payload;
};

const MasterDriverAddEdit = forwardRef(
  ({ open, id, onClose, onSuccess, onError, onSubmitStart = () => {}, onSubmitEnd = () => {} }, ref) => {
    const { t } = useLanguage();
    const [formKey, setFormKey] = useState(0);
    const [initialValues, setInitialValues] = useState(getBaseInitial);
    const formikRef = useRef();
    const notify = useNotifier();

    useImperativeHandle(ref, () => ({
      submitForm: () => formikRef.current?.submitForm?.(),
      validateForm: () => formikRef.current?.validateForm?.()
    }));

    // --- LOGIKA FETCH DATA & RESET FORM ---
    useEffect(() => {
      let mounted = true;

      if (!open) {
        const timer = setTimeout(() => {
          const fresh = getBaseInitial();
          setInitialValues(fresh);
          formikRef.current?.resetForm?.({ values: fresh });
        }, 500);
        return () => clearTimeout(timer);
      } else {
        // Increment formKey saat dialog dibuka, memastikan Formik dan child component ter-remount bersih
        setFormKey((prev) => prev + 1);
      }

      const fetch = async () => {
        // Jika sedang mode Tambah Data (id == null), bersihkan form
        if (!id) {
          const fresh = getBaseInitial();
          setInitialValues(fresh);
          formikRef.current?.resetForm?.({ values: fresh });
          return;
        }

        try {
          // --- HANYA DIJALANKAN JIKA MODE EDIT (id ada isinya) ---
          const data = await getDriverById(id);
          if (!mounted) return;

          if (data) {
            const fresh = getBaseInitial();
            setInitialValues({
              ...fresh,
              id: data.id ?? null,
              company: data.company ?? data.company_id ?? null,
              name: data.name ?? '',
              rfid: data.rfid ?? '',
              driver_code: data.driver_code ?? data.code ?? '',
              nik: data.nik ?? data.ktp_no ?? '',
              mobile_no: data.mobile_no ?? data.phone_no ?? '',
              phone_no: data.phone_no ?? '',
              email: data.email ?? '',
              birth_place: data.birth_place ?? data.place_of_birth ?? '',
              birth_date: toDateInput(data.birth_date ?? data.birthdate ?? data.tanggal_lahir ?? ''),
              hired_date: toDateInput(data.hired_date ?? data.hireddate ?? data.tanggal_perekrutan ?? ''),
              is_pod_active: !!data.is_pod_active,
              status: data.status === undefined ? true : !!data.status,
              image: data.image ?? data.photo ?? '',
              ktp_no: data.ktp_no ?? '',
              ktp_expdate: toDateInput(data.ktp_expdate ?? ''),
              license_no: data.license_no ?? '',
              license_type: data.license_type ?? '',
              latest_education: data.latest_education ?? '',
              license_exp_date: toDateInput(data.license_exp_date ?? data.license_expired ?? ''),
              driver_type: data.driver_type ?? '',
              address: data.address ?? '',
              driver_type_id: data.driver_type_id ?? null,
              vehicle: data.vehicle ?? null,
              driver_attachments: data.driver_attachments ?? fresh.driver_attachments,
              driver_license: Array.isArray(data.driver_license)
                ? data.driver_license.map((dl) => ({
                    id: dl.id ?? null,
                    data_back_photo: dl.data_back_photo,
                    data_front_photo: dl.data_front_photo,
                    driver_license: dl.driver_license ?? dl.license_type ?? dl.license_id ?? undefined,
                    file_license: dl.file_license,
                    front_photo: dl.front_photo,
                    back_photo: dl.back_photo,
                    license_exp_date: dl.license_exp_date ? toDateInput(dl.license_exp_date) : '',
                    license_name: dl.license_name,
                    license_no: dl.license_no ?? '',
                    url_license: dl.url_license,
                    is_lifetime: dl.is_lifetime
                  }))
                : []
            });
          }
        } catch (err) {
          if (mounted && id) {
            console.error('Failed to load driver data:', err);
            onError && onError(t('driverLicense.failedLoadDriver', 'Failed to load driver data'));
          }
        }
      };

      fetch();

      return () => {
        mounted = false;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, open]);

    const validationSchema = Yup.object().shape({
      company: Yup.mixed().required(t('validation.required', 'Company is required')),
      driver_code: Yup.string().required(t('validation.required', 'Driver Code is required')),
      name: Yup.string().required(t('validation.required', 'Driver name is required')),
      email: Yup.string()
        .email(t('validation.invalidEmail', 'Invalid email format'))
        .required(t('validation.required', 'Email is required'))
    });

    const uploadDriverPhoto = async (id, file) => {
      const formData = new FormData();
      formData.append('file', file);
      const url = `/driver/change-photo/${id}`;
      return await fleetApiGo.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
      setSubmitting(true);
      onSubmitStart();
      try {
        const removedLicenseIds = values.driver_license_removed || [];
        if (removedLicenseIds.length > 0) {
          const deletePromises = removedLicenseIds.map((licenseDetailId) =>
            fleetApi.delete(`/driver-license-detail/delete/${licenseDetailId}`)
          );
          await Promise.allSettled(deletePromises);
        }

        const filesToDelete = values._images_to_delete || [];
        if (filesToDelete.length > 0) {
          const uniqueFilesToDelete = filesToDelete.filter(
            (rf, index, self) =>
              rf?.id &&
              rf?.type &&
              rf?.fileName &&
              !removedLicenseIds.includes(rf.id) &&
              index === self.findIndex((t) => t.id === rf.id && t.type === rf.type && t.fileName === rf.fileName)
          );
          for (let rf of uniqueFilesToDelete) {
            await deleteDriverLicenseDetailImage(rf.id, rf.type, rf.fileName);
          }
        }

        const payload = normalizeDriverPayload(values, { forUpdate: !!id });
        let driverId = id;
        let response;

        if (!id && values.is_pod_active) {
          const podPayload = {
            company: parseInt(values.company),
            email: values.email,
            name: formatUpperCase(values.name),
            username: values.driver_code,
            password: '12345'
          };
          await fleetApi.post('/user-pod/new', podPayload);
        }

        if (id) {
          response = await updateDriver(id, payload);
        } else {
          response = await createDriver(payload);
          driverId = response?.data?.id || response?.id;
        }
        if (!driverId) throw new Error(t('driverLicense.idNotFound', 'Driver ID not found'));

        const freshData = await getDriverById(driverId);
        response = { data: freshData };

        const photoFile = values._image_file;
        if (driverId && photoFile) {
          await uploadDriverPhoto(driverId, photoFile);
        }

        const savedLicensesFromDB = response?.data?.driver_license || [];
        const formLicenses = values.driver_license || [];

        for (const item of formLicenses) {
          const dbMatch = savedLicensesFromDB.find((s) => String(s.license_no) === String(item.license_no));
          const licenseDetailId = item.id || dbMatch?.id;
          if (licenseDetailId) {
            if (item.upload_type === 'photo') {
              if (item.front_photo_file instanceof File) {
                await uploadDriverLicenseDetailFile(licenseDetailId, item.front_photo_file, 'front');
              }
              if (item.back_photo_file instanceof File) {
                await uploadDriverLicenseDetailFile(licenseDetailId, item.back_photo_file, 'back');
              }
            } else if (item.upload_type === 'document' && item.document_file instanceof File) {
              await uploadDriverLicenseDetailFile(licenseDetailId, item.document_file, 'document');
            }
          }
        }
        if (response?.code === 400 || response?.status === false || response?.error) {
          throw response;
        }
        onSuccess &&
          onSuccess(
            id
              ? t('driverLicense.updatedSuccessfully', 'Updated Successfully')
              : t('driverLicense.createdSuccessfully', 'Created Successfully')
          );
        const fresh = getBaseInitial();
        setInitialValues(fresh);
        resetForm({ values: fresh });
        formikRef.current?.resetForm?.({ values: fresh });
        onClose && onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage =
          err?.response?.data?.message || err?.data?.message || err?.message || t('driverLicense.failedSaveData', 'Failed to save data');
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
        onSubmitEnd();
      }
    };

    return (
      <Formik
        key={formKey}
        innerRef={formikRef}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formik) => {
          const handleCloseDialog = () => {
            const fresh = getBaseInitial();
            setInitialValues(fresh);
            formik.resetForm({ values: fresh });
            formikRef.current?.resetForm?.({ values: fresh });
            onClose && onClose();
          };

          return (
            <FormDialog
              open={open}
              onClose={handleCloseDialog}
              title={id ? t('masterDriver.edit', 'Edit Driver') : t('masterDriver.add', 'Add Driver')}
              fullScreenMode={true}
              fullHeight={false}
              actions={
                <>
                  <Button onClick={handleCloseDialog} disableRipple disableElevation variant="outlined">
                    {t('buttonTitle.cancel', 'Cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    form="master-driver-form"
                    disabled={formik.isSubmitting}
                    sx={{ backgroundColor: '#0072DA' }}
                    startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
                  >
                    {formik.isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
                  </Button>
                </>
              }
            >
              <Box>
                <form id="master-driver-form" onSubmit={formik.handleSubmit}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'start'
                        }}
                      >
                        <StorageIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                      </Box>
                      <Typography variant="h4">{t('driverLicense.formDriver', 'Form Driver')}</Typography>
                    </Box>
                  </Box>
                  <DriverForm formik={formik} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'start'
                        }}
                      >
                        <StorageIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                      </Box>
                      <Typography variant="h4">{t('driverLicense.licenseDriver', 'License Driver')}</Typography>
                    </Box>
                  </Box>
                  <DriverLicenseForm formik={formik} />
                </form>
              </Box>
            </FormDialog>
          );
        }}
      </Formik>
    );
  }
);

export default MasterDriverAddEdit;
